import { useState } from 'react';
import { AppHeader } from '../components/layout/AppHeader';
import { PageShell } from '../components/layout/PageShell';
import { CandidateTable } from '../components/market/CandidateTable';
import { EagleEyeHero } from '../components/market/EagleEyeHero';
import { FilterBar } from '../components/market/FilterBar';
import { Badge } from '../components/ui/Badge';
import { useMarketRadar } from '../hooks/useMarketRadar';
import { getFilters } from '../services/radarStore';
import type { RadarFilters } from '../types/market';

export function DashboardPage(): React.JSX.Element {
  // Initialize from the store so the chosen filters (e.g. horizon) survive
  // navigating to a coin page and back.
  const [filters, setFilters] = useState<RadarFilters>(() => getFilters());
  const radar = useMarketRadar(filters);

  return (
    <div className="app-shell">
      <AppHeader connectionLabel={radar.connectionLabel} />
      <PageShell>
        <div className="page-stack">
          <EagleEyeHero
            freshness={radar.freshness}
            warmupProgress={radar.warmupProgress}
            marketCount={radar.marketCount}
            diagnostics={radar.diagnostics}
            predictorRows={radar.predictor}
            monitoringRows={radar.monitoring}
          />
          <FilterBar filters={filters} onChange={setFilters} marketCount={radar.marketCount} />
          {radar.error && <div className="notice">{radar.error}</div>}
          <CandidateTable
            title="Predictor Candidates"
            description={`Selected horizon: ${filters.horizon}. Core-qualified predictions remain visible for their original horizon while live evidence updates; supporting methods affect priority, not Core authority. Ranking is stabilized to avoid rapid row jumping.`}
            rows={radar.predictor}
            mode="predictor"
            emptyMessage={radar.predictorEmptyMessage}
            action={(
              <div className="panel-action-stack">
                <Badge tone={radar.freshness === 'live' ? 'positive' : 'info'}>{radar.freshness}</Badge>
                <span>{radar.validationLabel}</span>
                <span>{radar.diagnostics.directionalObservations} directional · {radar.diagnostics.deepComplete + radar.diagnostics.deepPartial} deep</span>
              </div>
            )}
          />
          <CandidateTable
            title="Eagle Watch Monitoring"
            description="Continuous observations showing developing structure or near-close transitions, currently queued or monitored."
            rows={radar.monitoring}
            mode="monitoring"
            emptyMessage="No developing observation currently matches the selected filters."
            action={(
              <div className="panel-action-stack">
                <Badge tone="info">continuous monitoring</Badge>
                <span>Radar baseline {radar.warmupProgress}% ready</span>
                <span>{radar.diagnostics.neutralObservations} neutral · {radar.diagnostics.deepQueued} queued</span>
              </div>
            )}
          />
        </div>
      </PageShell>
    </div>
  );
}
